import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ProfileSkeleton } from '@/components/profile/ProfileSkeleton';
import { QualificationTab } from '@/components/profile/QualificationTab';
import { ObjectivesPanel } from '@/components/objectives/ObjectivesPanel';
import { KnowledgeFoldersSection, useKnowledgeIdentity } from '@/components/connections/KnowledgeFoldersSection';
import { KnowledgeDocumentDrawer } from '@/components/connections/KnowledgeDocumentDrawer';

type ProfileTab = 'qualification' | 'objectives' | 'documents';

/**
 * Managers see the objectives as step ② of the qualification (where they are set);
 * members, who do not qualify the company, keep them as a tab of their own.
 */
const TABS: Array<{ key: ProfileTab; label: string; managersOnly?: boolean; membersOnly?: boolean }> = [
  { key: 'qualification', label: 'Qualification', managersOnly: true },
  { key: 'objectives', label: 'Objectifs', membersOnly: true },
  { key: 'documents', label: 'Documents' },
];

/** « Profil » (route /profil): the company's qualification, its objectives and its documents. */
export const ContextPage = () => {
  const { userId, tenantId, isManager, ready } = useKnowledgeIdentity();
  const [params, setParams] = useSearchParams();
  // While the qualification loads, and before the first analysis, it is the whole page: no tabs.
  const [bare, setBare] = useState(true);
  // Right after signup (/bienvenue), the analysis starts on its own; the flag leaves the URL.
  const [startAnalysis] = useState(() => params.get('analyse') === '1');
  useEffect(() => {
    if (!params.has('analyse')) return;
    const next = new URLSearchParams(params);
    next.delete('analyse');
    setParams(next, { replace: true });
  }, [params, setParams]);
  const visible = TABS.filter((tab) => (!tab.managersOnly || isManager) && (!tab.membersOnly || !isManager));
  const requested = params.get('tab') as ProfileTab | null;
  // A link to the objectives opens step ② of the qualification for a manager.
  const objectivesStep = isManager && requested === 'objectives';
  const active: ProfileTab = objectivesStep ? 'qualification'
    : visible.some((tab) => tab.key === requested) ? requested! : isManager ? 'qualification' : 'objectives';

  return (
    <div className={cn('mx-auto p-8', !ready || active === 'qualification' ? 'max-w-7xl' : 'max-w-4xl')}>
      <h1 className="font-display text-2xl font-bold text-domino-ink">Profil</h1>
      {!ready && <div className="mt-6"><ProfileSkeleton /></div>}
      {ready && !(bare && active === 'qualification') && (
        <nav className="mt-4 flex gap-1 border-b border-domino-cardBorder" aria-label="Sections du profil">
          {visible.map((tab) => (
            <button
              key={tab.key}
              type="button"
              aria-current={active === tab.key ? 'page' : undefined}
              onClick={() => setParams({ tab: tab.key }, { replace: true })}
              className={cn(
                '-mb-px border-b-2 px-3 py-2 text-sm',
                active === tab.key ? 'border-domino-aubergine font-semibold text-domino-aubergine' : 'border-transparent text-domino-inkSoft hover:text-domino-ink',
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      )}
      {ready && <div className="mt-6">
        {active === 'qualification' && <QualificationTab requestedStep={objectivesStep ? 'objectives' : null} startAnalysis={startAnalysis} onBare={setBare} />}
        {active === 'objectives' && <ObjectivesPanel />}
        {active === 'documents' && (userId
          ? <KnowledgeFoldersSection userId={userId} tenantId={tenantId} isManager={isManager} />
          : <ProfileSkeleton />)}
      </div>}
      {active === 'documents' && params.get('doc') && (
        <KnowledgeDocumentDrawer documentId={params.get('doc')!} userId={userId} isManager={isManager} onClose={() => setParams({ tab: 'documents' }, { replace: true })} />
      )}
    </div>
  );
};
